import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import api from '../../services/api';

/*
  ResumoComercial.jsx

  Tela simples:
  - Mostra resumo comercial da empresa.
  - Permite cadastrar números administradores por canal.
  - Envia o resumo pelo canal selecionado.
  - Mostra erros na própria tela, de forma clara.

  Rotas usadas:
  GET  /api/whatsapp/canais
  GET  /api/canais/:canalWhatsappId/dashboard-resumos?periodo=diario|mensal
  POST /api/canais/:canalWhatsappId/send-summary

  Observação:
  - No envio, eles são mandados no body como numerosDestino.
  - Assim o backend não precisa de rota de config só para isso.
*/

const Icons = {
  Refresh: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 12a9 9 0 0 1-15 6.7L3 16" />
      <path d="M3 16h6v6" />
      <path d="M3 12a9 9 0 0 1 15-6.7L21 8" />
      <path d="M21 8h-6V2" />
    </svg>
  ),
  Send: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="m22 2-7 20-4-9-9-4Z" />
      <path d="M22 2 11 13" />
    </svg>
  ),
  Trophy: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" />
      <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" />
      <path d="M18 2H6v7a6 6 0 0 0 12 0V2z" />
      <path d="M4 22h16" />
    </svg>
  ),
  Store: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <path d="M9 22V12h6v10" />
    </svg>
  ),
  Receipt: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <path d="M14 2v6h6" />
      <path d="M16 13H8" />
      <path d="M16 17H8" />
    </svg>
  ),
  Whatsapp: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 11.5a8.38 8.38 0 0 1-12.47 7.3L3 21l2.25-5.35A8.38 8.38 0 1 1 21 11.5z" />
    </svg>
  ),
  UserPlus: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M19 8v6" />
      <path d="M22 11h-6" />
    </svg>
  ),
  X: () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round">
      <path d="M18 6 6 18" />
      <path d="m6 6 12 12" />
    </svg>
  ),
  Alert: () => (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <path d="M12 9v4" />
      <path d="M12 17h.01" />
    </svg>
  ),
  Check: () => (
    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 6 9 17l-5-5" />
    </svg>
  )
};

function safeToast(showToast, type, message) {
  if (typeof showToast === 'function') {
    showToast(type, message);
    return;
  }

  if (type === 'error') console.error(message);
  else console.log(message);
}

function normalizarId(valor) {
  if (!valor) return '';
  if (typeof valor === 'object') return String(valor._id || valor.id || valor.toString?.() || '');
  return String(valor);
}

function resolverArrayApi(data, keys = []) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.items)) return data.items;

  for (const key of keys) {
    if (Array.isArray(data?.[key])) return data[key];
  }

  return [];
}

function normalizarStatusCanal(canal = {}) {
  const status = String(canal?.runtime?.status || canal?.status || canal?.state || '').toUpperCase();

  if (canal?.runtime?.connected === true || canal?.connected === true) return 'CONNECTED';
  if (['CONNECTED', 'INCHAT', 'CHATSAVAILABLE', 'ISLOGGED'].includes(status)) return 'CONNECTED';
  if (['INITIALIZING', 'OPENING', 'LOADING'].includes(status)) return 'INITIALIZING';
  if (['QRCODE', 'QR_CODE', 'QR'].includes(status)) return 'QRCODE';

  return status || 'DISCONNECTED';
}

function normalizarCanal(canal = {}) {
  const id = normalizarId(canal._id || canal.id || canal.canalWhatsappId || canal.canalId);
  const empresaId = normalizarId(canal.empresaId?._id || canal.empresaId || canal.empresa?._id || canal.empresa?.id);
  const unidadeId = normalizarId(canal.unidadeId?._id || canal.unidadeId || canal.unidade?._id || canal.unidade?.id || empresaId);
  const status = normalizarStatusCanal(canal);

  return {
    id,
    empresaId,
    unidadeId,
    nome: canal.nome || canal.apelido || canal.sessionName || canal.numero || `Canal ${id.slice(-6)}`,
    unidadeNome:
      canal.unidadeId?.unidade ||
      canal.unidadeId?.nome ||
      canal.unidade?.nome ||
      canal.empresaId?.unidade ||
      canal.empresaId?.nome ||
      canal.unidadeNome ||
      canal.loja ||
      canal.codigoLoja ||
      'Unidade',
    codigoLoja: canal.codigoLoja || canal.loja || canal.unidadeId?.codigoLoja || canal.empresaId?.codigoLoja || '',
    numero: canal.numero || canal.telefone || canal.whatsapp || '',
    principal: canal.principal === true,
    status,
    connected: status === 'CONNECTED'
  };
}

function parseMoedaBruta(valor) {
  if (valor === null || valor === undefined || valor === '') return 0;

  if (typeof valor === 'number') {
    return Number.isFinite(valor) ? valor : 0;
  }

  const texto = String(valor).trim();

  if (!texto) return 0;

  const semMoeda = texto.replace(/R\$/gi, '').trim();

  /*
    Quando já vem em formato brasileiro, preservamos o decimal:
    "21.860,42" => 21860.42
    "21860,42"  => 21860.42
  */
  if (semMoeda.includes(',')) {
    const numeroBr = Number(
      semMoeda
        .replace(/[^\d,.-]/g, '')
        .replace(/\./g, '')
        .replace(',', '.')
    );

    return Number.isNaN(numeroBr) ? 0 : numeroBr;
  }

  /*
    Quando vem em formato técnico com ponto decimal:
    "21860.42" => 21860.42
  */
  if (/^-?\d+\.\d{1,2}$/.test(semMoeda)) {
    const numeroDecimal = Number(semMoeda.replace(/[^\d.-]/g, ''));
    return Number.isNaN(numeroDecimal) ? 0 : numeroDecimal;
  }

  const somenteDigitos = semMoeda.replace(/\D/g, '');

  if (!somenteDigitos) return 0;

  const inteiro = Number(somenteDigitos);

  if (Number.isNaN(inteiro)) return 0;

  /*
    Correção principal:
    A API pode retornar valores monetários em centavos.
    Exemplo real:
    2186042 precisa virar R$ 21.860,42, e não R$ 2.186.042,00.
  */
  if (somenteDigitos.length >= 6) {
    return inteiro / 100;
  }

  return inteiro;
}

function formatarMoeda(valor) {
  const numero = parseMoedaBruta(valor);

  return numero.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  });
}

function numeroMoeda(valor) {
  return parseMoedaBruta(valor);
}

function normalizarTelefone(valor = '') {
  let numero = String(valor || '').replace(/\D/g, '');

  if (!numero) return '';

  if (numero.startsWith('55') && numero.length >= 12) return numero;

  if (numero.length === 10 || numero.length === 11) {
    return `55${numero}`;
  }

  return numero;
}

function formatarTelefone(numero = '') {
  const clean = String(numero || '').replace(/\D/g, '');

  if (clean.startsWith('55') && clean.length >= 12) {
    const semPais = clean.slice(2);

    if (semPais.length === 11) {
      return semPais.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
    }

    if (semPais.length === 10) {
      return semPais.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
    }
  }

  if (clean.length === 11) {
    return clean.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
  }

  if (clean.length === 10) {
    return clean.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
  }

  return clean || 'Número inválido';
}


function montarErroDetalhado(error, fallback = 'Erro ao processar solicitação.') {
  const response = error?.response;
  const data = response?.data || {};
  const status = response?.status;

  return {
    id: `${Date.now()}_${Math.random().toString(16).slice(2)}`,
    tipo: 'error',
    titulo: status ? `Erro ${status}` : 'Erro',
    mensagem: data?.message || data?.error || error?.message || fallback,
    status,
    metodo: response?.config?.method ? String(response.config.method).toUpperCase() : '',
    rota: response?.config?.url || '',
    detalhe:
      data?.details ||
      data?.detail ||
      data?.stack ||
      (typeof data === 'string' ? data : '')
  };
}

function montarSucesso(mensagem) {
  return {
    id: `${Date.now()}_${Math.random().toString(16).slice(2)}`,
    tipo: 'success',
    titulo: 'Tudo certo',
    mensagem
  };
}

function montarPayloadCanal(canal) {
  return {
    canalWhatsappId: canal.id,
    canalId: canal.id,
    whatsappChannelId: canal.id,
    empresaCanalId: canal.empresaId || undefined,
    empresaOperacionalId: canal.empresaId || undefined,
    unidadeId: canal.unidadeId || canal.empresaId || undefined,
    codigoLoja: canal.codigoLoja || undefined,
    loja: canal.codigoLoja || undefined,
    forcarCanalManual: true,
    canalManual: true,
    origem: 'RESUMO_COMERCIAL_MANUAL'
  };
}


function extrairNumerosAdminApi(data = {}) {
  const origem =
    data.numerosAdmin ||
    data.numeroAdmin ||
    data.data?.numerosAdmin ||
    data.config?.numerosAdmin ||
    data.config?.numeroAdmin ||
    [];

  if (Array.isArray(origem)) {
    return origem.map(normalizarTelefone).filter(Boolean);
  }

  if (typeof origem === 'string') {
    return origem.split(',').map(normalizarTelefone).filter(Boolean);
  }

  return [];
}

export default function ResumoComercial({ showToast }) {
  const avisoTimersRef = useRef({});

  const [loading, setLoading] = useState(true);
  const [loadingResumo, setLoadingResumo] = useState(false);
  const [sendingTipo, setSendingTipo] = useState('');
  const [periodo, setPeriodo] = useState('diario');
  const [canais, setCanais] = useState([]);
  const [canalSelecionadoId, setCanalSelecionadoId] = useState('');
  const [erro, setErro] = useState('');
  const [avisos, setAvisos] = useState([]);
  const [adminInput, setAdminInput] = useState('');
  const [numerosAdmin, setNumerosAdmin] = useState([]);
  const [resumo, setResumo] = useState({
    placares: [],
    lojas: [],
    ultimasVendas: []
  });

  const canalSelecionado = useMemo(() => (
    canais.find((canal) => canal.id === canalSelecionadoId) || null
  ), [canais, canalSelecionadoId]);

  const totalVendas = useMemo(() => {
    if (resumo.lojas.length) {
      return resumo.lojas.reduce((acc, loja) => acc + Number(loja.vendas || loja.quantidade || 0), 0);
    }

    return resumo.ultimasVendas.length;
  }, [resumo]);

  const totalValor = useMemo(() => {
    if (resumo.lojas.length) {
      return resumo.lojas.reduce((acc, loja) => acc + numeroMoeda(loja.valor || loja.total || 0), 0);
    }

    return resumo.ultimasVendas.reduce((acc, venda) => acc + numeroMoeda(venda.valor || venda.total || 0), 0);
  }, [resumo]);

  const rotaCanal = useCallback((canalId, endpoint) => (
    `/api/canais/${canalId}/${String(endpoint || '').replace(/^\/+/, '')}`
  ), []);

  const carregarAdminsCanal = useCallback(async (canalId) => {
    if (!canalId) {
      setNumerosAdmin([]);
      return [];
    }

    try {
      const { data } = await api.get(rotaCanal(canalId, 'resumo-admins'));
      const lista = extrairNumerosAdminApi(data);

      setNumerosAdmin(lista);
      return lista;
    } catch (error) {
      const erroDetalhado = montarErroDetalhado(error, 'Não foi possível carregar os administradores do resumo.');

      setNumerosAdmin([]);
      setAvisos((prev) => [erroDetalhado, ...prev].slice(0, 4));
      return [];
    }
  }, [rotaCanal]);

  const limparAviso = useCallback((id) => {
    setAvisos((prev) => prev.filter((aviso) => aviso.id !== id));

    if (avisoTimersRef.current[id]) {
      clearTimeout(avisoTimersRef.current[id]);
      delete avisoTimersRef.current[id];
    }
  }, []);

  const adicionarAviso = useCallback((aviso, options = {}) => {
    const tempo =
      typeof options.tempo === 'number'
        ? options.tempo
        : aviso?.tipo === 'success'
          ? 4200
          : 6500;

    const avisoFinal = {
      ...aviso,
      id: aviso.id || `${Date.now()}_${Math.random().toString(16).slice(2)}`,
      criadoEm: Date.now(),
      tempo
    };

    setAvisos((prev) => [avisoFinal, ...prev].slice(0, 4));

    if (avisoTimersRef.current[avisoFinal.id]) {
      clearTimeout(avisoTimersRef.current[avisoFinal.id]);
    }

    avisoTimersRef.current[avisoFinal.id] = setTimeout(() => {
      setAvisos((prev) => prev.filter((item) => item.id !== avisoFinal.id));
      delete avisoTimersRef.current[avisoFinal.id];
    }, tempo);
  }, []);

  const escolherCanalPadrao = useCallback((lista = []) => (
    lista.find((canal) => canal.principal && canal.connected)?.id ||
    lista.find((canal) => canal.connected)?.id ||
    lista.find((canal) => canal.principal)?.id ||
    lista[0]?.id ||
    ''
  ), []);

  const carregarResumo = useCallback(async (canalId, periodoAtual = periodo) => {
    if (!canalId) {
      setResumo({ placares: [], lojas: [], ultimasVendas: [] });
      return;
    }

    setLoadingResumo(true);
    setErro('');

    try {
      const { data } = await api.get(rotaCanal(canalId, 'dashboard-resumos'), {
        params: { periodo: periodoAtual }
      });

      setResumo({
        placares: data?.placares || data?.data?.placares || [],
        lojas: data?.lojas || data?.data?.lojas || [],
        ultimasVendas: data?.ultimasVendas || data?.data?.ultimasVendas || []
      });
    } catch (error) {
      const erroDetalhado = montarErroDetalhado(error, 'Não foi possível carregar o resumo comercial.');

      setErro(erroDetalhado.mensagem);
      adicionarAviso(erroDetalhado);
      setResumo({ placares: [], lojas: [], ultimasVendas: [] });
    } finally {
      setLoadingResumo(false);
    }
  }, [adicionarAviso, periodo, rotaCanal]);

  const iniciarTela = useCallback(async () => {
    setLoading(true);
    setErro('');

    try {
      const { data } = await api.get('/api/whatsapp/canais');
      const lista = resolverArrayApi(data, ['canais', 'channels'])
        .map(normalizarCanal)
        .filter((canal) => canal.id);

      setCanais(lista);

      const canalPadrao = escolherCanalPadrao(lista);
      setCanalSelecionadoId(canalPadrao);

      if (canalPadrao) {
        await carregarAdminsCanal(canalPadrao);
        await carregarResumo(canalPadrao, periodo);
      }
    } catch (error) {
      const erroDetalhado = montarErroDetalhado(error, 'Não foi possível carregar os canais da empresa.');

      setErro(erroDetalhado.mensagem);
      adicionarAviso(erroDetalhado);
    } finally {
      setLoading(false);
    }
  }, [adicionarAviso, carregarAdminsCanal, carregarResumo, escolherCanalPadrao, periodo]);

  useEffect(() => {
    iniciarTela();
  }, [iniciarTela]);

  useEffect(() => () => {
    Object.values(avisoTimersRef.current).forEach(clearTimeout);
    avisoTimersRef.current = {};
  }, []);

  const handleTrocarCanal = async (canalId) => {
    setCanalSelecionadoId(canalId);

    if (canalId) {
      await carregarAdminsCanal(canalId);
      await carregarResumo(canalId, periodo);
    } else {
      setNumerosAdmin([]);
    }
  };

  const handleTrocarPeriodo = async (novoPeriodo) => {
    setPeriodo(novoPeriodo);

    if (canalSelecionadoId) await carregarResumo(canalSelecionadoId, novoPeriodo);
  };

  const handleAtualizar = async () => {
    if (!canalSelecionadoId) {
      await iniciarTela();
      return;
    }

    await carregarResumo(canalSelecionadoId, periodo);
  };

  const handleAdicionarAdmin = async () => {
    const numero = normalizarTelefone(adminInput);

    if (!canalSelecionadoId) {
      adicionarAviso({
        id: `${Date.now()}_no_channel_admin`,
        tipo: 'error',
        titulo: 'Canal não selecionado',
        mensagem: 'Selecione um canal antes de salvar o administrador.'
      });
      return;
    }

    if (!numero || numero.length < 12) {
      adicionarAviso({
        id: `${Date.now()}_invalid_phone`,
        tipo: 'error',
        titulo: 'Número inválido',
        mensagem: 'Informe um WhatsApp válido com DDD. Exemplo: 92981176885.'
      });
      return;
    }

    if (numerosAdmin.includes(numero)) {
      adicionarAviso({
        id: `${Date.now()}_duplicate_phone`,
        tipo: 'error',
        titulo: 'Número já adicionado',
        mensagem: 'Esse administrador já está na lista de recebimento.'
      });
      return;
    }

    try {
      const { data } = await api.post(
        rotaCanal(canalSelecionadoId, 'resumo-admins'),
        { numero }
      );

      const novaLista = extrairNumerosAdminApi(data);
      setNumerosAdmin(novaLista);
      setAdminInput('');

      adicionarAviso(montarSucesso(data?.message || 'Administrador adicionado à lista de envio.'));
    } catch (error) {
      adicionarAviso(montarErroDetalhado(error, 'Erro ao salvar administrador.'));
    }
  };

  const handleRemoverAdmin = async (numero) => {
    if (!canalSelecionadoId) return;

    try {
      const { data } = await api.delete(
        rotaCanal(canalSelecionadoId, 'resumo-admins'),
        { data: { numero } }
      );

      const novaLista = extrairNumerosAdminApi(data);
      setNumerosAdmin(novaLista);

      adicionarAviso(montarSucesso(data?.message || 'Administrador removido da lista.'));
    } catch (error) {
      adicionarAviso(montarErroDetalhado(error, 'Erro ao remover administrador.'));
    }
  };

  const handleEnviarResumo = async (tipo) => {
    if (!canalSelecionado) {
      adicionarAviso({
        id: `${Date.now()}_no_channel`,
        tipo: 'error',
        titulo: 'Canal não selecionado',
        mensagem: 'Selecione um canal de WhatsApp para enviar o resumo.'
      });
      return;
    }

    if (!canalSelecionado.connected) {
      adicionarAviso({
        id: `${Date.now()}_offline_channel`,
        tipo: 'error',
        titulo: 'Canal desconectado',
        mensagem: 'O canal selecionado está desconectado. Conecte o WhatsApp antes de enviar.'
      });
      return;
    }

    if (!numerosAdmin.length) {
      adicionarAviso({
        id: `${Date.now()}_no_admins`,
        tipo: 'error',
        titulo: 'Nenhum administrador cadastrado',
        mensagem: 'Adicione pelo menos um número administrador para receber o resumo.'
      });
      return;
    }

    setSendingTipo(tipo);

    try {
      const { data } = await api.post(
        rotaCanal(canalSelecionado.id, 'send-summary'),
        {
          ...montarPayloadCanal(canalSelecionado),
          type: tipo,
          tipo,
          periodo,
          numerosDestino: numerosAdmin,
          numeroDestino: numerosAdmin.join(','),
          numerosAdmin
        }
      );

      if (data?.success === false) {
        const aviso = {
          id: `${Date.now()}_api_false`,
          tipo: 'error',
          titulo: 'Resumo não enviado',
          mensagem: data?.message || 'O backend recusou o envio do resumo.',
          detalhe: data?.error || ''
        };

        adicionarAviso(aviso);
        safeToast(showToast, 'error', aviso.mensagem);
      } else {
        const mensagem = data?.message || 'Resumo enviado com sucesso.';

        adicionarAviso(montarSucesso(mensagem));
        safeToast(showToast, 'success', mensagem);
      }
    } catch (error) {
      const erroDetalhado = montarErroDetalhado(error, 'Erro ao enviar resumo.');

      adicionarAviso(erroDetalhado);
      safeToast(showToast, 'error', erroDetalhado.mensagem);
    } finally {
      setSendingTipo('');
    }
  };

  if (loading) {
    return (
      <div className="resumo-page loading">
        <div className="loader" />
        <strong>Carregando resumo comercial...</strong>
        <style>{styles}</style>
      </div>
    );
  }

  return (
    <div className="resumo-page">
      <header className="resumo-header">
        <div>
          <h1>Resumo comercial</h1>
          <p>Acompanhe vendas, vendedores, lojas e envie o resumo pelo canal selecionado.</p>
        </div>

        <button type="button" className="soft-btn" onClick={handleAtualizar} disabled={loadingResumo}>
          <Icons.Refresh />
          {loadingResumo ? 'Atualizando...' : 'Atualizar'}
        </button>
      </header>

      {avisos.length > 0 && (
        <section className="alerts-stack">
          {avisos.map((aviso) => (
            <article className={`alert-card ${aviso.tipo === 'success' ? 'success' : 'error'}`} key={aviso.id}>
              <div className="alert-icon">
                {aviso.tipo === 'success' ? <Icons.Check /> : <Icons.Alert />}
              </div>

              <div className="alert-content">
                <strong>{aviso.titulo}</strong>
                <p>{aviso.mensagem}</p>

                {(aviso.rota || aviso.metodo || aviso.status || aviso.detalhe) && (
                  <div className="alert-meta">
                    {aviso.status && <span>Status: {aviso.status}</span>}
                    {aviso.metodo && <span>Método: {aviso.metodo}</span>}
                    {aviso.rota && <span>Rota: {aviso.rota}</span>}
                    {aviso.detalhe && <span>Detalhe: {String(aviso.detalhe).slice(0, 240)}</span>}
                  </div>
                )}
                <div
                  className="alert-progress"
                  style={{
                    animationDuration: `${aviso.tempo || 5000}ms`
                  }}
                />
              </div>

              <button type="button" className="alert-close" onClick={() => limparAviso(aviso.id)}>
                <Icons.X />
              </button>
            </article>
          ))}
        </section>
      )}

      <section className="control-card">
        <div className="field">
          <label>Período</label>
          <div className="segmented">
            <button type="button" className={periodo === 'diario' ? 'active' : ''} onClick={() => handleTrocarPeriodo('diario')}>
              Diário
            </button>
            <button type="button" className={periodo === 'mensal' ? 'active' : ''} onClick={() => handleTrocarPeriodo('mensal')}>
              Mensal
            </button>
          </div>
        </div>

        <div className="field grow">
          <label>Canal para envio</label>
          <select value={canalSelecionadoId} onChange={(event) => handleTrocarCanal(event.target.value)}>
            <option value="">Selecione um canal</option>
            {canais.map((canal) => (
              <option key={canal.id} value={canal.id}>
                {canal.connected ? '🟢' : '⚪'} {canal.unidadeNome} · {canal.nome}
              </option>
            ))}
          </select>
        </div>

        <div className={`canal-chip ${canalSelecionado?.connected ? 'online' : ''}`}>
          <Icons.Whatsapp />
          <div>
            <strong>{canalSelecionado?.nome || 'Nenhum canal'}</strong>
            <span>
              {canalSelecionado
                ? `${canalSelecionado.unidadeNome} · ${canalSelecionado.connected ? 'conectado' : 'desconectado'}`
                : `${canais.filter((c) => c.connected).length}/${canais.length} conectados`}
            </span>
          </div>
        </div>
      </section>

      <section className="admin-card">
        <div className="admin-head">
          <div>
            <h2><Icons.UserPlus /> Administradores do resumo</h2>
            <p>Esses números receberão o placar, resumo por loja ou extrato enviado pelo canal selecionado.</p>
          </div>

          <div className="admin-count">
            <strong>{numerosAdmin.length}</strong>
            <span>número(s)</span>
          </div>
        </div>

        <div className="admin-input-row">
          <input
            value={adminInput}
            onChange={(event) => setAdminInput(event.target.value)}
            onKeyDown={(event) => event.key === 'Enter' && handleAdicionarAdmin()}
            placeholder="Digite o WhatsApp com DDD. Ex: 92981176885"
          />

          <button type="button" className="soft-btn dark" onClick={handleAdicionarAdmin}>
            Adicionar
          </button>
        </div>

        <div className="admin-list">
          {numerosAdmin.length === 0 && (
            <div className="empty small">
              Nenhum administrador cadastrado. Adicione pelo menos um número para conseguir enviar o resumo.
            </div>
          )}

          {numerosAdmin.map((numero) => (
            <span className="admin-chip" key={numero}>
              {formatarTelefone(numero)}
              <button type="button" onClick={() => handleRemoverAdmin(numero)}>
                <Icons.X />
              </button>
            </span>
          ))}
        </div>
      </section>

      {erro && <div className="error-box">{erro}</div>}

      <section className="metrics-strip">
        <div className="metric-item">
          <span>Vendas</span>
          <strong>{totalVendas}</strong>
          <small>{periodo === 'mensal' ? 'no mês' : 'hoje'}</small>
        </div>
        <div className="metric-item">
          <span>Faturamento</span>
          <strong>{formatarMoeda(totalValor)}</strong>
          <small>{periodo === 'mensal' ? 'período mensal' : 'período diário'}</small>
        </div>
        <div className="metric-item">
          <span>Vendedores</span>
          <strong>{resumo.placares.length}</strong>
          <small>com venda no período</small>
        </div>
        <div className="metric-item">
          <span>Lojas</span>
          <strong>{resumo.lojas.length}</strong>
          <small>unidades com movimento</small>
        </div>
      </section>

      <main className="content-grid">
        <section className="panel">
          <div className="panel-head">
            <div>
              <h2><Icons.Trophy /> Placar de vendedores</h2>
              <p>Ranking do período selecionado.</p>
            </div>
            <button type="button" className="send-btn" onClick={() => handleEnviarResumo('placar')} disabled={sendingTipo === 'placar'}>
              <Icons.Send />
              {sendingTipo === 'placar' ? 'Enviando...' : 'Enviar'}
            </button>
          </div>

          <div className="list">
            {loadingResumo && <div className="empty">Carregando dados...</div>}
            {!loadingResumo && resumo.placares.length === 0 && <div className="empty">Nenhum vendedor com venda no período.</div>}
            {!loadingResumo && resumo.placares.map((item, index) => (
              <div className="rank-row" key={item.id || item.vendedor || item.nome || index}>
                <em>{index + 1}º</em>
                <div>
                  <strong>{item.vendedor || item.nome || 'Vendedor'}</strong>
                  <span>{item.vendas || item.quantidade || 0} venda(s)</span>
                </div>
                <b>{formatarMoeda(item.valor ?? item.total ?? 0)}</b>
              </div>
            ))}
          </div>
        </section>

        <section className="panel">
          <div className="panel-head">
            <div>
              <h2><Icons.Store /> Resumo por loja</h2>
              <p>Desempenho por unidade da empresa.</p>
            </div>
            <button type="button" className="send-btn" onClick={() => handleEnviarResumo('lojas')} disabled={sendingTipo === 'lojas'}>
              <Icons.Send />
              {sendingTipo === 'lojas' ? 'Enviando...' : 'Enviar'}
            </button>
          </div>

          <div className="store-list">
            {loadingResumo && <div className="empty">Carregando dados...</div>}
            {!loadingResumo && resumo.lojas.length === 0 && <div className="empty">Nenhuma loja com venda no período.</div>}
            {!loadingResumo && resumo.lojas.map((loja, index) => (
              <div className="store-row" key={loja.id || loja.nome || loja.loja || index}>
                <div>
                  <strong>{loja.nome || loja.loja || 'Loja'}</strong>
                  <span>{loja.vendas || loja.quantidade || 0} venda(s)</span>
                </div>
                <b>{formatarMoeda(loja.valor ?? loja.total ?? 0)}</b>
              </div>
            ))}
          </div>
        </section>
      </main>

      <section className="panel full">
        <div className="panel-head">
          <div>
            <h2><Icons.Receipt /> Últimas vendas</h2>
            <p>Extrato do período selecionado.</p>
          </div>
          <button type="button" className="send-btn" onClick={() => handleEnviarResumo('extrato')} disabled={sendingTipo === 'extrato'}>
            <Icons.Send />
            {sendingTipo === 'extrato' ? 'Enviando...' : 'Enviar'}
          </button>
        </div>

        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>OS</th>
                <th>Loja</th>
                <th>Vendedor</th>
                <th>Valor</th>
                <th>Data</th>
              </tr>
            </thead>
            <tbody>
              {loadingResumo && (
                <tr>
                  <td colSpan="5" className="center">Carregando dados...</td>
                </tr>
              )}
              {!loadingResumo && resumo.ultimasVendas.length === 0 && (
                <tr>
                  <td colSpan="5" className="center">Nenhuma venda encontrada.</td>
                </tr>
              )}
              {!loadingResumo && resumo.ultimasVendas.map((venda, index) => (
                <tr key={`${venda.os || venda.numeroOS || index}-${index}`}>
                  <td>#{venda.os || venda.numeroOS || '-'}</td>
                  <td>{venda.loja || venda.codigoLoja || '-'}</td>
                  <td>{venda.vendedor || '-'}</td>
                  <td><strong>{formatarMoeda(venda.valor ?? venda.total ?? 0)}</strong></td>
                  <td>{venda.data || venda.createdAt || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <style>{styles}</style>
    </div>
  );
}

const styles = `
  :root {
    --bg: #f8fafc;
    --card: #fff;
    --border: #e5e7eb;
    --soft-border: #eef2f7;
    --text: #0f172a;
    --muted: #64748b;
    --blue: #2563eb;
    --green: #16a34a;
    --red: #dc2626;
    --amber: #d97706;
    --radius: 16px;
  }

  * { box-sizing: border-box; }

  button, select, input {
    font-family: inherit;
  }

  .resumo-page {
    width: 100%;
    min-height: 100%;
    background: var(--bg);
    color: var(--text);
    padding: 16px;
    font-family: Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  }

  .resumo-page.loading {
    min-height: 70vh;
    display: grid;
    place-items: center;
    align-content: center;
    gap: 10px;
    text-align: center;
  }

  .loader {
    width: 30px;
    height: 30px;
    border: 3px solid #e2e8f0;
    border-top-color: var(--blue);
    border-radius: 999px;
    animation: spin .8s linear infinite;
  }

  @keyframes spin { to { transform: rotate(360deg); } }

  .resumo-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 14px;
    padding-bottom: 14px;
    margin-bottom: 12px;
    border-bottom: 1px solid var(--border);
  }

  .kicker,
  .section-kicker {
    display: inline-flex;
    width: fit-content;
    border: 1px solid #dbeafe;
    background: #eff6ff;
    color: #1d4ed8;
    border-radius: 999px;
    padding: 4px 8px;
    font-size: 10px;
    font-weight: 900;
    letter-spacing: .06em;
    text-transform: uppercase;
  }

  .resumo-header h1 {
    margin: 8px 0 4px;
    font-size: clamp(23px, 3vw, 31px);
    line-height: 1;
    font-weight: 850;
    letter-spacing: -.045em;
  }

  .resumo-header p {
    margin: 0;
    max-width: 760px;
    color: var(--muted);
    font-size: 13px;
    line-height: 1.45;
  }

  .soft-btn, .send-btn {
    min-height: 36px;
    border: 1px solid var(--border);
    background: #fff;
    color: #334155;
    border-radius: 999px;
    padding: 0 13px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 7px;
    cursor: pointer;
    font-size: 12px;
    font-weight: 850;
    white-space: nowrap;
  }

  .soft-btn.dark,
  .send-btn {
    border-color: #0f172a;
    background: #0f172a;
    color: #fff;
  }

  .soft-btn:hover { background: #f8fafc; }
  .soft-btn.dark:hover,
  .send-btn:hover { background: #1e293b; }

  button:disabled {
    opacity: .55;
    cursor: not-allowed;
  }

  .alerts-stack {
    display: grid;
    gap: 8px;
    margin-bottom: 12px;
  }

  .alert-card {
    border: 1px solid var(--border);
    border-radius: 16px;
    background: #fff;
    padding: 11px;
    display: grid;
    grid-template-columns: 34px minmax(0, 1fr) 28px;
    gap: 10px;
    align-items: flex-start;
  }

  .alert-card.error {
    border-color: #fecaca;
    background: #fff7f7;
  }

  .alert-card.success {
    border-color: #bbf7d0;
    background: #f0fdf4;
  }

  .alert-icon {
    width: 34px;
    height: 34px;
    border-radius: 12px;
    display: grid;
    place-items: center;
    background: #fee2e2;
    color: #b91c1c;
  }

  .alert-card.success .alert-icon {
    background: #dcfce7;
    color: #15803d;
  }

  .alert-content strong {
    display: block;
    font-size: 13px;
    font-weight: 850;
  }

  .alert-content p {
    margin: 3px 0 0;
    color: #475569;
    font-size: 12.5px;
    line-height: 1.4;
  }

  .alert-progress {
    margin-top: 9px;
    height: 3px;
    width: 100%;
    border-radius: 999px;
    background: currentColor;
    opacity: .35;
    transform-origin: left;
    animation-name: alertShrink;
    animation-timing-function: linear;
    animation-fill-mode: forwards;
  }

  @keyframes alertShrink {
    from { transform: scaleX(1); }
    to { transform: scaleX(0); }
  }

  .alert-meta {
    margin-top: 8px;
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }

  .alert-meta span {
    border: 1px solid var(--border);
    background: #fff;
    color: #64748b;
    border-radius: 999px;
    padding: 4px 8px;
    font-size: 10.5px;
    font-weight: 750;
  }

  .alert-close {
    width: 28px;
    height: 28px;
    border: 0;
    border-radius: 999px;
    background: transparent;
    color: #64748b;
    cursor: pointer;
    display: grid;
    place-items: center;
  }

  .alert-close:hover {
    background: rgba(15, 23, 42, .06);
  }

  .control-card,
  .admin-card {
    margin-bottom: 12px;
    border: 1px solid var(--border);
    border-radius: var(--radius);
    background: #fff;
    padding: 12px;
  }

  .control-card {
    display: grid;
    grid-template-columns: auto minmax(260px, 1fr) auto;
    gap: 10px;
    align-items: end;
  }

  .field {
    display: grid;
    gap: 6px;
  }

  .field.grow {
    min-width: 0;
  }

  label {
    color: #334155;
    font-size: 11px;
    line-height: 1;
    font-weight: 850;
  }

  select,
  input {
    width: 100%;
    min-height: 38px;
    border: 1px solid var(--border);
    border-radius: 12px;
    background: #fff;
    color: var(--text);
    outline: none;
    padding: 0 11px;
    font-size: 12.5px;
    font-weight: 600;
  }

  select:focus,
  input:focus {
    border-color: #bfdbfe;
    box-shadow: 0 0 0 3px rgba(37, 99, 235, .10);
  }

  .segmented {
    display: inline-flex;
    gap: 4px;
    border: 1px solid var(--border);
    background: #f8fafc;
    border-radius: 999px;
    padding: 4px;
  }

  .segmented button {
    min-height: 30px;
    border: 0;
    background: transparent;
    color: var(--muted);
    border-radius: 999px;
    padding: 0 12px;
    cursor: pointer;
    font-size: 12px;
    font-weight: 850;
  }

  .segmented button.active {
    background: #fff;
    color: var(--text);
    box-shadow: 0 1px 2px rgba(15, 23, 42, .06);
  }

  .canal-chip {
    min-height: 42px;
    border: 1px solid var(--border);
    border-radius: 14px;
    background: #fff;
    color: #475569;
    padding: 7px 11px;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .canal-chip.online {
    border-color: #bbf7d0;
    background: #f0fdf4;
    color: #166534;
  }

  .canal-chip strong,
  .canal-chip span {
    display: block;
  }

  .canal-chip strong {
    max-width: 220px;
    font-size: 12.5px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .canal-chip span {
    margin-top: 2px;
    color: var(--muted);
    font-size: 11px;
  }

  .admin-card {
    display: grid;
    gap: 10px;
  }

  .admin-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
  }

  .admin-head h2 {
    margin: 7px 0 4px;
    display: flex;
    align-items: center;
    gap: 7px;
    font-size: 16px;
    font-weight: 850;
    letter-spacing: -.03em;
  }

  .admin-head p {
    margin: 0;
    color: var(--muted);
    font-size: 12px;
    line-height: 1.45;
  }

  .admin-count {
    min-width: 82px;
    border: 1px solid var(--soft-border);
    background: #f8fafc;
    border-radius: 14px;
    padding: 10px;
    text-align: center;
  }

  .admin-count strong,
  .admin-count span {
    display: block;
  }

  .admin-count strong {
    font-size: 20px;
    line-height: 1;
    font-weight: 900;
  }

  .admin-count span {
    margin-top: 4px;
    color: var(--muted);
    font-size: 11px;
    font-weight: 750;
  }

  .admin-input-row {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 8px;
  }

  .admin-list {
    display: flex;
    flex-wrap: wrap;
    gap: 7px;
  }

  .admin-chip {
    min-height: 31px;
    border: 1px solid var(--border);
    background: #fff;
    border-radius: 999px;
    padding: 0 5px 0 10px;
    display: inline-flex;
    align-items: center;
    gap: 7px;
    font-size: 12px;
    font-weight: 800;
  }

  .admin-chip button {
    width: 22px;
    height: 22px;
    border: 0;
    border-radius: 999px;
    background: #f1f5f9;
    color: #64748b;
    display: grid;
    place-items: center;
    cursor: pointer;
  }

  .admin-chip button:hover {
    background: #fee2e2;
    color: #b91c1c;
  }

  .error-box {
    margin-bottom: 12px;
    border: 1px solid #fecaca;
    background: #fef2f2;
    color: #b91c1c;
    border-radius: 14px;
    padding: 10px 12px;
    font-size: 12px;
    font-weight: 750;
  }

  .metrics-grid {
    margin-bottom: 12px;
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 10px;
  }

  .metric-card, .panel {
    border: 1px solid var(--border);
    border-radius: var(--radius);
    background: #fff;
    padding: 14px;
  }

  .metric-card span,
  .metric-card small {
    display: block;
  }

  .metric-card span {
    color: var(--muted);
    font-size: 11px;
    font-weight: 850;
    text-transform: uppercase;
    letter-spacing: .04em;
  }

  .metric-card strong {
    display: block;
    margin-top: 7px;
    font-size: 22px;
    line-height: 1;
    font-weight: 900;
    letter-spacing: -.04em;
  }

  .metric-card small {
    margin-top: 5px;
    color: var(--muted);
    font-size: 11.5px;
  }

  .content-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
    margin-bottom: 12px;
  }

  .panel.full {
    margin-bottom: 12px;
  }

  .panel-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 12px;
  }

  .panel-head h2 {
    margin: 0;
    display: flex;
    align-items: center;
    gap: 7px;
    font-size: 16px;
    font-weight: 850;
    letter-spacing: -.03em;
  }

  .panel-head p {
    margin: 4px 0 0;
    color: var(--muted);
    font-size: 12px;
  }

  .list, .store-list {
    display: grid;
    gap: 8px;
  }

  .rank-row, .store-row {
    border: 1px solid var(--soft-border);
    border-radius: 13px;
    background: #fff;
    padding: 10px;
  }

  .rank-row {
    display: grid;
    grid-template-columns: 38px minmax(0, 1fr) auto;
    gap: 10px;
    align-items: center;
  }

  .rank-row em {
    width: 32px;
    height: 32px;
    border-radius: 999px;
    background: #eff6ff;
    color: #1d4ed8;
    display: grid;
    place-items: center;
    font-size: 12px;
    font-style: normal;
    font-weight: 900;
  }

  .rank-row strong,
  .rank-row span,
  .store-row strong,
  .store-row span {
    display: block;
  }

  .rank-row strong,
  .store-row strong {
    font-size: 13px;
  }

  .rank-row span,
  .store-row span {
    margin-top: 2px;
    color: var(--muted);
    font-size: 11.5px;
  }

  .rank-row b,
  .store-row b {
    font-size: 13px;
    font-weight: 900;
  }

  .store-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
  }

  .empty {
    width: 100%;
    border: 1px dashed var(--border);
    background: #f8fafc;
    border-radius: 12px;
    padding: 14px;
    text-align: center;
    color: var(--muted);
    font-size: 12px;
  }

  .empty.small {
    padding: 10px;
    text-align: left;
  }

  .table-wrap {
    overflow-x: auto;
    border: 1px solid var(--border);
    border-radius: 14px;
  }

  table {
    width: 100%;
    min-width: 680px;
    border-collapse: collapse;
    background: #fff;
  }

  th, td {
    padding: 10px 12px;
    border-bottom: 1px solid var(--soft-border);
    text-align: left;
    font-size: 12px;
  }

  th {
    background: #f8fafc;
    color: var(--muted);
    font-size: 10px;
    font-weight: 900;
    text-transform: uppercase;
    letter-spacing: .05em;
  }

  tr:last-child td {
    border-bottom: 0;
  }

  .center {
    text-align: center;
    color: var(--muted);
  }

  @media (max-width: 980px) {
    .control-card, .content-grid, .metrics-grid {
      grid-template-columns: 1fr;
    }

    .panel-head, .resumo-header, .admin-head {
      display: grid;
    }

    .admin-count {
      text-align: left;
    }
  }

  @media (max-width: 640px) {
    .resumo-page {
      padding: 10px;
      background: #fff;
    }

    .soft-btn, .send-btn {
      width: 100%;
    }

    .segmented {
      width: 100%;
    }

    .segmented button {
      flex: 1;
    }

    .admin-input-row,
    .store-row {
      display: grid;
      grid-template-columns: 1fr;
    }

    table {
      min-width: 560px;
    }
  }

  /* =========================================================================
     V2 — PADRÃO GLOBAL DA PLATAFORMA
     Tela branca, plana e integrada ao dashboard.
  ========================================================================= */

  :root {
    --bg: #ffffff;
    --card: #ffffff;
    --border: #e5e5e5;
    --soft-border: #eeeeee;
    --text: #171717;
    --muted: #747474;
    --blue: #111111;
    --green: #16803c;
    --red: #c9362b;
    --amber: #b26b00;
    --radius: 10px;
  }

  .resumo-page {
    width: 100%;
    min-height: 100%;
    padding: 18px 24px 34px !important;
    color: #171717 !important;
    background: #ffffff !important;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Inter, Arial, sans-serif !important;
    -webkit-font-smoothing: antialiased;
    text-rendering: geometricPrecision;
  }

  .resumo-page.loading {
    min-height: 60vh !important;
    background: #ffffff !important;
  }

  .loader {
    width: 24px !important;
    height: 24px !important;
    border: 2px solid #dddddd !important;
    border-top-color: #111111 !important;
  }

  .resumo-header {
    min-height: 48px;
    margin: 0 0 16px !important;
    padding: 0 0 14px !important;
    border-bottom: 1px solid #ececec !important;
    align-items: center !important;
  }

  .kicker,
  .section-kicker {
    display: none !important;
  }

  .resumo-header h1 {
    margin: 0 0 3px !important;
    color: #171717 !important;
    font-size: 18px !important;
    line-height: 1.2 !important;
    font-weight: 700 !important;
    letter-spacing: -.025em !important;
  }

  .resumo-header p {
    max-width: none !important;
    color: #747474 !important;
    font-size: 11.5px !important;
    line-height: 1.4 !important;
  }

  .soft-btn,
  .send-btn {
    min-height: 34px !important;
    padding: 0 11px !important;
    border-radius: 7px !important;
    font-size: 11px !important;
    font-weight: 650 !important;
    box-shadow: none !important;
    transition: none !important;
  }

  .soft-btn {
    border: 1px solid #d8d8d8 !important;
    color: #333333 !important;
    background: #ffffff !important;
  }

  .soft-btn.dark,
  .send-btn {
    border: 1px solid #111111 !important;
    color: #ffffff !important;
    background: #111111 !important;
  }

  .soft-btn:hover,
  .soft-btn.dark:hover,
  .send-btn:hover {
    transform: none !important;
  }

  .alerts-stack {
    gap: 6px !important;
    margin-bottom: 12px !important;
  }

  .alert-card {
    padding: 9px 10px !important;
    grid-template-columns: 28px minmax(0, 1fr) 26px !important;
    gap: 8px !important;
    border-radius: 8px !important;
    box-shadow: none !important;
  }

  .alert-icon {
    width: 28px !important;
    height: 28px !important;
    border-radius: 6px !important;
  }

  .alert-content strong {
    font-size: 11.5px !important;
    font-weight: 700 !important;
  }

  .alert-content p {
    font-size: 11px !important;
  }

  .alert-meta span {
    border-radius: 6px !important;
    font-size: 9.5px !important;
  }

  .control-card {
    grid-template-columns: auto minmax(240px, 1fr) minmax(180px, auto) !important;
    gap: 10px !important;
    align-items: end !important;
    margin-bottom: 14px !important;
    padding: 0 0 14px !important;
    border: 0 !important;
    border-bottom: 1px solid #ececec !important;
    border-radius: 0 !important;
    background: transparent !important;
    box-shadow: none !important;
  }

  .field {
    gap: 4px !important;
  }

  label {
    color: #686868 !important;
    font-size: 10px !important;
    font-weight: 700 !important;
    letter-spacing: .04em;
    text-transform: uppercase;
  }

  select,
  input {
    min-height: 34px !important;
    padding: 0 9px !important;
    border: 1px solid #d8d8d8 !important;
    border-radius: 7px !important;
    color: #171717 !important;
    background: #ffffff !important;
    font-size: 11.5px !important;
    font-weight: 600 !important;
    box-shadow: none !important;
  }

  select:focus,
  input:focus {
    border-color: #111111 !important;
    box-shadow: 0 0 0 2px rgba(0,0,0,.05) !important;
  }

  .segmented {
    gap: 0 !important;
    padding: 0 !important;
    border: 1px solid #d8d8d8 !important;
    border-radius: 7px !important;
    background: #ffffff !important;
  }

  .segmented button {
    min-height: 32px !important;
    padding: 0 11px !important;
    border-radius: 0 !important;
    color: #666666 !important;
    font-size: 11px !important;
    font-weight: 650 !important;
  }

  .segmented button + button {
    border-left: 1px solid #d8d8d8 !important;
  }

  .segmented button.active {
    color: #ffffff !important;
    background: #111111 !important;
    box-shadow: none !important;
  }

  .canal-chip {
    min-height: 34px !important;
    padding: 5px 9px !important;
    border: 1px solid #d8d8d8 !important;
    border-radius: 7px !important;
    color: #444444 !important;
    background: #ffffff !important;
    box-shadow: none !important;
  }

  .canal-chip.online {
    border-color: #cfcfcf !important;
    color: #1f5f36 !important;
    background: #f7faf8 !important;
  }

  .canal-chip strong {
    font-size: 11px !important;
  }

  .canal-chip span {
    font-size: 9.5px !important;
  }

  .admin-card {
    margin-bottom: 14px !important;
    padding: 0 0 14px !important;
    border: 0 !important;
    border-bottom: 1px solid #ececec !important;
    border-radius: 0 !important;
    background: transparent !important;
    box-shadow: none !important;
  }

  .admin-head {
    align-items: center !important;
  }

  .admin-head h2 {
    margin: 0 0 3px !important;
    color: #171717 !important;
    font-size: 13px !important;
    font-weight: 700 !important;
    letter-spacing: -.015em !important;
  }

  .admin-head p {
    color: #777777 !important;
    font-size: 10.5px !important;
  }

  .admin-count {
    min-width: 68px !important;
    padding: 7px 9px !important;
    border: 1px solid #dddddd !important;
    border-radius: 7px !important;
    background: #ffffff !important;
    box-shadow: none !important;
  }

  .admin-count strong {
    font-size: 16px !important;
    font-weight: 700 !important;
  }

  .admin-count span {
    font-size: 9.5px !important;
  }

  .admin-chip {
    min-height: 28px !important;
    padding: 0 4px 0 8px !important;
    border-radius: 7px !important;
    font-size: 10.5px !important;
    font-weight: 650 !important;
  }

  .admin-chip button {
    width: 20px !important;
    height: 20px !important;
    border-radius: 5px !important;
  }

  .error-box {
    border-radius: 7px !important;
    font-size: 11px !important;
    font-weight: 650 !important;
  }

  .metrics-strip {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 0;
    margin-bottom: 14px;
    border-top: 1px solid #ececec;
    border-bottom: 1px solid #ececec;
    background: #ffffff;
  }

  .metric-item {
    min-height: 64px;
    padding: 11px 14px;
    border-right: 1px solid #ececec;
    background: #ffffff;
  }

  .metric-item:last-child {
    border-right: 0;
  }

  .metric-item span,
  .metric-item small {
    display: block;
  }

  .metric-item span {
    color: #777777;
    font-size: 9.5px;
    font-weight: 700;
    letter-spacing: .04em;
    text-transform: uppercase;
  }

  .metric-item strong {
    display: block;
    margin-top: 5px;
    color: #171717;
    font-size: 20px;
    line-height: 1;
    font-weight: 700;
    letter-spacing: -.03em;
  }

  .metric-item small {
    margin-top: 4px;
    color: #888888;
    font-size: 9.5px;
  }

  .content-grid {
    grid-template-columns: 1fr 1fr !important;
    gap: 0 !important;
    margin-bottom: 0 !important;
    border-top: 1px solid #e8e8e8;
    border-bottom: 1px solid #e8e8e8;
  }

  .panel {
    padding: 0 !important;
    border: 0 !important;
    border-radius: 0 !important;
    background: #ffffff !important;
    box-shadow: none !important;
  }

  .content-grid .panel:first-child {
    border-right: 1px solid #e8e8e8 !important;
  }

  .panel.full {
    margin-bottom: 0 !important;
    border-bottom: 1px solid #e8e8e8 !important;
  }

  .panel-head {
    min-height: 52px;
    margin: 0 !important;
    padding: 10px 12px !important;
    border-bottom: 1px solid #ececec !important;
    align-items: center !important;
  }

  .panel-head h2 {
    color: #171717 !important;
    font-size: 12.5px !important;
    font-weight: 700 !important;
    letter-spacing: -.015em !important;
  }

  .panel-head p {
    margin-top: 2px !important;
    color: #777777 !important;
    font-size: 10px !important;
  }

  .list,
  .store-list {
    gap: 0 !important;
  }

  .rank-row,
  .store-row {
    padding: 9px 11px !important;
    border: 0 !important;
    border-bottom: 1px solid #eeeeee !important;
    border-radius: 0 !important;
    background: #ffffff !important;
    box-shadow: none !important;
  }

  .rank-row em {
    width: 26px !important;
    height: 26px !important;
    border-radius: 6px !important;
    color: #171717 !important;
    background: #eeeeee !important;
    font-size: 10px !important;
    font-weight: 700 !important;
  }

  .rank-row strong,
  .store-row strong {
    font-size: 11px !important;
  }

  .rank-row span,
  .store-row span {
    font-size: 9.5px !important;
  }

  .rank-row b,
  .store-row b {
    font-size: 11px !important;
    font-weight: 700 !important;
  }

  .empty {
    border: 0 !important;
    border-radius: 0 !important;
    color: #777777 !important;
    background: #ffffff !important;
    font-size: 10.5px !important;
  }

  .table-wrap {
    border: 0 !important;
    border-radius: 0 !important;
  }

  table {
    min-width: 680px;
  }

  th,
  td {
    padding: 9px 11px !important;
    border-bottom: 1px solid #eeeeee !important;
    font-size: 10.5px !important;
  }

  th {
    color: #777777 !important;
    background: #f7f7f7 !important;
    font-size: 9px !important;
    font-weight: 700 !important;
  }

  @media (max-width: 980px) {
    .control-card,
    .content-grid {
      grid-template-columns: 1fr !important;
    }

    .content-grid .panel:first-child {
      border-right: 0 !important;
      border-bottom: 1px solid #e8e8e8 !important;
    }
  }

  @media (max-width: 640px) {
    .resumo-page {
      padding: 14px 12px 24px !important;
    }

    .resumo-header,
    .admin-head {
      display: grid !important;
    }

    .metrics-strip {
      grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
    }

    .metric-item:nth-child(2) {
      border-right: 0;
    }

    .metric-item:nth-child(-n+2) {
      border-bottom: 1px solid #ececec;
    }
  }

`;